import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Input } from '../../ui/Input';
import { Select } from '../../ui/Select';
import { SearchableSelect } from '../../ui/SearchableSelect';
import { Button } from '../../ui/Button';
import { useTeachers } from '../../../hooks/useTeachers';
import { teacherAssignmentStatuses } from '../../../data/trainingCourseOptions';
import { formatEuro, parseAmount } from '../../../utils/currency';
import { isTeacherEnabled, teacherFullName } from '../../../utils/teachers';
import { teacherLineTotal } from '../../../utils/trainingCourses';
import { CourseErrors, CourseTeacher, TeacherAssignmentStatus } from '../../../types/trainingCourse';
interface CourseTeachersFieldsProps {
  teachers: CourseTeacher[];
  onChange: (teachers: CourseTeacher[]) => void;
  errors: CourseErrors;
  // Product of the course: only teachers enabled for it are proposed
  productCode?: string;
}
export function CourseTeachersFields({
  teachers,
  onChange,
  errors,
  productCode
}: CourseTeachersFieldsProps) {
  const allTeachers = useTeachers();
  const enabledCount = allTeachers.filter(t => isTeacherEnabled(t, productCode)).length;
  const total = teachers.reduce((s, t) => s + teacherLineTotal(t), 0);
  const updateRow = (index: number, changes: Partial<CourseTeacher>) => onChange(teachers.map((t, i) => i === index ? {
    ...t,
    ...changes
  } : t));
  const selectTeacher = (index: number, teacherId: string) => {
    const teacher = allTeachers.find(t => t.id === teacherId);
    // Pre-fill the hourly cost from the teacher's rate
    updateRow(index, {
      teacherId,
      hourlyRate: teachers[index].hourlyRate ?? teacher?.hourlyRate
    });
  };
  const addRow = () => onChange([...teachers, {
    teacherId: '',
    assignmentStatus: 'Da inviare'
  }]);
  const removeRow = (index: number) => onChange(teachers.filter((_, i) => i !== index));
  return <div className="space-y-4" data-id="element-1189" data-mp="9gf1kipj3mjb">
      <p className="text-xs text-slate-500" data-id="element-1190" data-mp="9goofxfa1dks">
        {productCode ? `Sono proposti solo i docenti abilitati a ${productCode} nell'anagrafica docenti (${enabledCount}).` : "Scegli la tipologia di corso per vedere i docenti abilitati."}
      </p>
      {teachers.length === 0 && <p className="text-sm text-slate-500" data-id="element-1191" data-mp="9c4s38f1995l">
          Nessun docente assegnato al corso.
        </p>}
      {teachers.map((row, index) => {
      const selected = allTeachers.find(t => t.id === row.teacherId);
      const notEnabled = selected ? !isTeacherEnabled(selected, productCode) : false;
      const options = allTeachers.filter(t => t.id === row.teacherId || isTeacherEnabled(t, productCode) && !teachers.some(o => o.teacherId === t.id)).map(t => ({
        value: t.id,
        label: `${teacherFullName(t)} · ${formatEuro(t.hourlyRate)}/h${isTeacherEnabled(t, productCode) ? '' : ' · non abilitato'}`
      }));
      return <div key={row.teacherId || `new-${index}`} className="rounded-xl border border-slate-200 p-4" data-id="element-1192" data-mp="1oa0qdfim40v2" data-mpi={index}>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-start" data-id="element-1193" data-mp="1xbgzljxoxwxp">
              <div className="md:col-span-6" data-id="element-1194" data-mp="1x9dxi1j9kdcm">
                <SearchableSelect label="Docente" value={row.teacherId} onChange={e => selectTeacher(index, e.target.value)} options={options} placeholder="Cerca in anagrafica docenti..." error={errors[`teacher-${index}`]} required data-id="element-1195" data-mp="22atk3y13arx4" />
                {notEnabled && <p className="text-xs text-amber-700 mt-1.5" data-id="element-1196" data-mp="1jcqzrwjhlj5n">
                    Il docente non è abilitato per il prodotto di questo corso.
                  </p>}
              </div>
              <div className="md:col-span-5" data-id="element-1197" data-mp="qud3i1dsu58o">
                <Select label="Stato incarico" value={row.assignmentStatus} onChange={e => updateRow(index, {
              assignmentStatus: e.target.value as TeacherAssignmentStatus
            })} options={teacherAssignmentStatuses.map(s => ({
              value: s,
              label: s
            }))} data-id="element-1198" data-mp="qt1yjy8marfz" />
              </div>
              <div className="md:col-span-1 flex md:justify-end md:pt-7" data-id="element-1199" data-mp="379zvtz1tvb00">
                <Button type="button" variant="light" color="danger" size="sm" className="!px-2" aria-label="Rimuovi docente" onClick={() => removeRow(index)} data-id="element-1200" data-mp="37bum7svvbvbh">
                  <Trash2 className="h-4 w-4" data-id="element-1201" data-mp="1mtmvmf708fnd" />
                </Button>
              </div>
              <div className="md:col-span-4" data-id="element-1202" data-mp="1mvznbk2ke4dm">
                <Input label="Costo orario" type="number" min={0} step="0.5" value={row.hourlyRate ?? ''} onChange={e => updateRow(index, {
              hourlyRate: parseAmount(e.target.value)
            })} endContent={<span className="text-xs" data-id="element-1204" data-mp="17wn2kpyr25fa">€/h</span>} data-id="element-1203" data-mp="1cqrabbfuahhf" />
              </div>
              <div className="md:col-span-4" data-id="element-1205" data-mp="t3tmb2bnll9k">
                <Input label="Ore" type="number" min={0} step="0.5" value={row.hours ?? ''} onChange={e => updateRow(index, {
              hours: parseAmount(e.target.value)
            })} data-id="element-1206" data-mp="y1277ao6s3ei" />
              </div>
              <div className="md:col-span-4" data-id="element-1207" data-mp="2ecz3gysyi1ek">
                <span className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-1208" data-mp="2jeeq2vas8fz2">
                  Importo totale
                </span>
                <div className="rounded-xl bg-slate-100 px-4 py-2.5 text-sm font-semibold text-seform-navy tabular-nums" data-id="element-1209" data-mp="2h0t2ia2nymby">
                  {formatEuro(teacherLineTotal(row))}
                </div>
              </div>
            </div>
          </div>;
    })}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-2" data-id="element-1210" data-mp="230ictad3bdsd">
        <Button type="button" variant="flat" color="secondary" startContent={<Plus className="h-4 w-4" data-id="element-1212" data-mp="2vpfga7cn7612" />} onClick={addRow} data-id="element-1211" data-mp="30nqqij6unijt">
          Aggiungi docente
        </Button>
        <p className="text-sm text-slate-500" data-id="element-1213" data-mp="2qw34wywz4vq9">
          Importo complessivo docenza
          <span className="ml-2 text-base font-semibold text-seform-navy tabular-nums" data-id="element-1214" data-mp="2lxjk51u49pzh">
            {formatEuro(total)}
          </span>
        </p>
      </div>
    </div>;
}